/**
 * Structure draft — the client-side model of a reorganization being planned.
 *
 * A draft is a private copy of the tree. The admin edits it freely; nothing
 * touches the live structure until `authorize_structure_revision` (mig 732).
 * Every node of the base revision that the draft no longer contains must be
 * given a disposition before authorization:
 *
 *   merged_into  one successor     — standing references carry automatically
 *   split_into   two or more       — a human places each standing reference
 *   retired      none              — "unmapped" is a legitimate final answer
 *
 * Nodes that are still in the draft (renamed, moved, or untouched) continue;
 * they need no mapping. The server owns validity — these helpers keep the UI
 * from proposing a tree the server would refuse, they do not replace it.
 *
 * Spec: docs/9-exploration/PRD-structure-versioning-and-transitions.md FR-2/3.
 */

export interface DraftNode {
  nodeId: string;
  parentNodeId: string | null;
  name: string;
  level: string | null;
  sortOrder: number;
}

export type DepartureDisposition = 'merged_into' | 'split_into' | 'retired';

export interface DraftDisposition {
  fromNodeId: string;
  disposition: DepartureDisposition;
  successorIds: string[];
  note?: string;
}

export class DraftEditError extends Error {}

function descendantsOf(nodes: readonly DraftNode[], nodeId: string): Set<string> {
  const out = new Set<string>();
  const queue = [nodeId];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    for (const n of nodes) {
      if (n.parentNodeId === cur && !out.has(n.nodeId)) {
        out.add(n.nodeId);
        queue.push(n.nodeId);
      }
    }
  }
  return out;
}

function assertSiblingNameFree(nodes: readonly DraftNode[], parentNodeId: string | null, name: string, exceptId?: string) {
  const clash = nodes.some(
    (n) => n.nodeId !== exceptId && n.parentNodeId === parentNodeId && n.name.trim().toLowerCase() === name.trim().toLowerCase(),
  );
  if (clash) throw new DraftEditError(`Another unit under the same parent is already called "${name.trim()}".`);
}

export function renameDraftNode(nodes: readonly DraftNode[], nodeId: string, name: string): DraftNode[] {
  const trimmed = name.trim();
  if (!trimmed) throw new DraftEditError('A unit needs a name.');
  const node = nodes.find((n) => n.nodeId === nodeId);
  if (!node) throw new DraftEditError('That unit is not in the draft.');
  assertSiblingNameFree(nodes, node.parentNodeId, trimmed, nodeId);
  return nodes.map((n) => (n.nodeId === nodeId ? { ...n, name: trimmed } : n));
}

export function moveDraftNode(nodes: readonly DraftNode[], nodeId: string, newParentId: string | null): DraftNode[] {
  const node = nodes.find((n) => n.nodeId === nodeId);
  if (!node) throw new DraftEditError('That unit is not in the draft.');
  if (newParentId === nodeId) throw new DraftEditError('A unit cannot sit under itself.');
  if (newParentId && !nodes.some((n) => n.nodeId === newParentId)) throw new DraftEditError('The new parent is not in the draft.');
  if (newParentId && descendantsOf(nodes, nodeId).has(newParentId)) {
    throw new DraftEditError('A unit cannot be moved under one of its own sub-units.');
  }
  assertSiblingNameFree(nodes, newParentId, node.name, nodeId);
  return nodes.map((n) => (n.nodeId === nodeId ? { ...n, parentNodeId: newParentId } : n));
}

export function addDraftNode(
  nodes: readonly DraftNode[],
  input: { nodeId: string; parentNodeId: string | null; name: string; level: string | null },
): DraftNode[] {
  const trimmed = input.name.trim();
  if (!trimmed) throw new DraftEditError('A unit needs a name.');
  if (input.parentNodeId && !nodes.some((n) => n.nodeId === input.parentNodeId)) {
    throw new DraftEditError('The parent is not in the draft.');
  }
  assertSiblingNameFree(nodes, input.parentNodeId, trimmed);
  return [...nodes, { nodeId: input.nodeId, parentNodeId: input.parentNodeId, name: trimmed, level: input.level, sortOrder: 0 }];
}

/** Removes one unit. Its sub-units must be moved or removed first — a removal never takes a subtree with it silently. */
export function removeDraftNode(nodes: readonly DraftNode[], nodeId: string): DraftNode[] {
  if (nodes.some((n) => n.parentNodeId === nodeId)) {
    throw new DraftEditError('Move or remove the units under this one first.');
  }
  return nodes.filter((n) => n.nodeId !== nodeId);
}

/** Puts a removed base unit back where it was (or at the root's level if its old parent has gone). */
export function restoreDraftNode(nodes: readonly DraftNode[], base: readonly DraftNode[], nodeId: string): DraftNode[] {
  const original = base.find((n) => n.nodeId === nodeId);
  if (!original) throw new DraftEditError('That unit was not part of the current structure.');
  if (nodes.some((n) => n.nodeId === nodeId)) return [...nodes];
  const parentStillThere = original.parentNodeId && nodes.some((n) => n.nodeId === original.parentNodeId);
  const root = nodes.find((n) => n.parentNodeId === null);
  const parentNodeId = parentStillThere ? original.parentNodeId : root?.nodeId ?? null;
  assertSiblingNameFree(nodes, parentNodeId, original.name);
  return [...nodes, { ...original, parentNodeId }];
}

/** Base units the draft no longer contains — each needs a disposition. */
export function departedNodes(base: readonly DraftNode[], draft: readonly DraftNode[]): DraftNode[] {
  const kept = new Set(draft.map((n) => n.nodeId));
  return base.filter((n) => !kept.has(n.nodeId));
}

export interface DraftProblem {
  nodeId?: string;
  message: string;
}

/** Everything that would stop this draft from being authorized, in plain words. */
export function draftProblems(
  base: readonly DraftNode[],
  draft: readonly DraftNode[],
  dispositions: readonly DraftDisposition[],
  options: { singleRoot: boolean },
): DraftProblem[] {
  const problems: DraftProblem[] = [];
  const roots = draft.filter((n) => n.parentNodeId === null);
  if (draft.length === 0) problems.push({ message: 'The structure is empty.' });
  if (options.singleRoot && roots.length !== 1) problems.push({ message: 'An org chart has exactly one top-level unit.' });

  const inDraft = new Set(draft.map((n) => n.nodeId));
  const byFrom = new Map(dispositions.map((d) => [d.fromNodeId, d]));
  for (const n of departedNodes(base, draft)) {
    const d = byFrom.get(n.nodeId);
    if (!d) {
      problems.push({ nodeId: n.nodeId, message: `"${n.name}" leaves the structure — say what becomes of it.` });
      continue;
    }
    const live = d.successorIds.filter((id) => inDraft.has(id));
    if (d.disposition === 'merged_into' && live.length !== 1) {
      problems.push({ nodeId: n.nodeId, message: `"${n.name}" is merged — pick the one unit it merges into.` });
    } else if (d.disposition === 'split_into' && live.length < 2) {
      problems.push({ nodeId: n.nodeId, message: `"${n.name}" is split — pick at least two units it splits into.` });
    }
  }
  return problems;
}

/** Drops dispositions for units that are back in the draft and successors that have left it. */
export function pruneDispositions(
  base: readonly DraftNode[],
  draft: readonly DraftNode[],
  dispositions: readonly DraftDisposition[],
): DraftDisposition[] {
  const gone = new Set(departedNodes(base, draft).map((n) => n.nodeId));
  const inDraft = new Set(draft.map((n) => n.nodeId));
  return dispositions
    .filter((d) => gone.has(d.fromNodeId))
    .map((d) => ({
      ...d,
      successorIds: d.disposition === 'retired' ? [] : d.successorIds.filter((id) => inDraft.has(id)),
    }));
}

/** Depth-first order with depth, for an indented list. */
export function orderDraftAsTree(nodes: readonly DraftNode[]): Array<DraftNode & { depth: number }> {
  const out: Array<DraftNode & { depth: number }> = [];
  const ids = new Set(nodes.map((n) => n.nodeId));
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number) => {
    const kids = nodes
      .filter((n) => (parent === null ? n.parentNodeId === null || !ids.has(n.parentNodeId) : n.parentNodeId === parent))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    for (const k of kids) {
      if (seen.has(k.nodeId)) continue;
      seen.add(k.nodeId);
      out.push({ ...k, depth });
      walk(k.nodeId, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}
